import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaFileAlt, FaFilePdf, FaFileImage, FaDownload, FaTimes, FaSpinner } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { useToast } from '../utils/toastContext';

const docTypeColor = {
    'ID': { bg: 'linear-gradient(135deg,#dbeafe 0%,#bfdbfe 100%)', color: '#1d4ed8', border: '#93c5fd' },
    'Passport': { bg: 'linear-gradient(135deg,#d1fae5 0%,#a7f3d0 100%)', color: '#065f46', border: '#6ee7b7' },
    'Bank Letter': { bg: 'linear-gradient(135deg,#ede9fe 0%,#ddd6fe 100%)', color: '#5b21b6', border: '#c4b5fd' },
};

function FileIcon({ contentType }) {
    if (!contentType) return <FaFileAlt />;
    if (contentType === 'application/pdf') return <FaFilePdf style={{ color: '#dc2626' }} />;
    return <FaFileImage style={{ color: '#0284c7' }} />;
}

export default function ViewUserDocumentsModal({ isOpen, onClose, userId, userName }) {
    const { showError } = useToast();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        if (!isOpen || !userId) return;
        setLoading(true);
        axiosInstance.get(`/crud/get_user_documents/${userId}`)
            .then(res => setDocs(res.data || []))
            .catch(() => showError('Failed to load documents'))
            .finally(() => setLoading(false));
    }, [isOpen, userId]);

    const handleDownload = async (docId, filename) => {
        try {
            setDownloadingId(docId);
            const res = await axiosInstance.get(`/crud/download_user_document/${docId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            showError('Failed to download document');
        } finally {
            setDownloadingId(null);
        }
    };

    if (!isOpen) return null;

    const modal = (
        <div
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(15,23,42,0.55)',
                backdropFilter: 'blur(4px)',
                zIndex: 20000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem'
            }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                background: 'white',
                borderRadius: '18px',
                boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    background: 'linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)'
                }}>
                    <div style={{
                        width: '36px', height: '36px', borderRadius: '10px',
                        background: 'linear-gradient(135deg,#2b4663 0%,#4a90e2 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: '1rem', flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(43,70,99,0.2)'
                    }}>
                        <FaFileAlt />
                    </div>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                            Documents
                        </h2>
                        {userName && (
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                                {userName}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#94a3b8', fontSize: '1.1rem', padding: '0.25rem',
                            borderRadius: '6px', transition: 'color 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#475569'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '1.5rem', marginBottom: '0.75rem', display: 'block', margin: '0 auto 0.75rem' }} />
                            Loading documents...
                        </div>
                    ) : docs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                            <FaFileAlt style={{ fontSize: '2.5rem', opacity: 0.3, marginBottom: '0.75rem' }} />
                            <p style={{ margin: 0, fontWeight: 500 }}>No documents found for this user</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {docs.map(doc => {
                                const colors = docTypeColor[doc.doc_type] || { bg: '#f8fafc', color: '#374151', border: '#e2e8f0' };
                                const isDownloading = downloadingId === doc.doc_id;
                                return (
                                    <div key={doc.doc_id} style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '1rem 1.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        background: '#fafafa',
                                        transition: 'box-shadow 0.15s'
                                    }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '10px',
                                            background: colors.bg,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '1.2rem', flexShrink: 0,
                                            border: `1px solid ${colors.border}`
                                        }}>
                                            <FileIcon contentType={doc.content_type} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                <span style={{
                                                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
                                                    padding: '0.15rem 0.55rem', borderRadius: '20px',
                                                    background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`
                                                }}>
                                                    {doc.doc_type}
                                                </span>
                                                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {doc.filename}
                                                </span>
                                            </div>
                                            {doc.upload_date && (
                                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                                                    {new Date(doc.upload_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleDownload(doc.doc_id, doc.filename)}
                                            disabled={isDownloading}
                                            title="Download"
                                            style={{
                                                padding: '0.5rem 0.875rem',
                                                border: '1.5px solid #3b82f6',
                                                borderRadius: '8px',
                                                background: 'white',
                                                color: '#3b82f6',
                                                fontSize: '0.8rem',
                                                cursor: isDownloading ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                                                fontWeight: 600, flexShrink: 0,
                                                opacity: isDownloading ? 0.6 : 1,
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => { if (!isDownloading) e.currentTarget.style.background = '#eff6ff'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                                        >
                                            {isDownloading
                                                ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                                : <FaDownload />
                                            }
                                            Download
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex', justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '0.625rem 1.5rem',
                            background: 'linear-gradient(135deg,#f1f5f9 0%,#e2e8f0 100%)',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            color: '#475569',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#e2e8f0 0%,#cbd5e1 100%)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#f1f5f9 0%,#e2e8f0 100%)'; }}
                    >
                        Close
                    </button>
                </div>
            </div>
            <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
}
